/**
 * Proposal E-Signature — legally-binding ESIGN/UETA-style capture with a
 * hash-chained signer audit trail and a signed-artifact certificate (PURE).
 *
 * Fixes the auditor's #1 finding ("e-sign is fake") and the "ledger sealing
 * not proven / no signed-artifact hash" findings:
 *
 *   • Consent + intent — a signature is captured ONLY with `consent === true`
 *     and a non-empty typed/drawn value (the ESIGN "intent to sign" +
 *     "consent to do business electronically" pair). Anything else throws.
 *   • Identity binding — every {@link SignatureRecord} carries the signer's
 *     identity, IP, and user agent, recorded verbatim from the caller
 *     (never fabricated here).
 *   • Document binding — every record embeds the `docVersionHash` produced
 *     by proposalBlocks' `snapshotVersion`, so "what exactly did they sign"
 *     is a hash equality check ({@link assertDocUnchangedSince}), not a vibe.
 *   • Tamper evidence — records form a hash chain: each `entryHash` digests
 *     the previous entry's hash + the doc hash + the signer's identity +
 *     the signature value + timestamp + origin, via an internal
 *     stableStringify. {@link verifySignatureChain} recomputes every link;
 *     editing ANY field of ANY record breaks the chain at that index.
 *   • Multi-signer ordering — 'sequential' enforces lowest-order-first;
 *     'parallel' lets any unsigned signer sign ({@link nextEligibleSigners}).
 *   • Certificate of completion — {@link buildCertificate} produces the
 *     countersigned artifact competitors ship as a PDF: every signer with
 *     signedAt/ip/method, the chain verdict, and a deterministic
 *     `certificateHash` over the whole thing.
 *
 * Deliberately pure: no store, no firebase, no auth, no ambient clocks —
 * timestamps/ip/userAgent arrive on the {@link SignatureIntent}. This module
 * is NOT provenance.ts (generic proposal WHY) and NOT proposalLifecycle.ts
 * (status machine): it owns the signature act itself.
 */

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

/** A party expected to sign, with their position in the signing order. */
export interface Signer {
  id: string;
  name: string;
  email: string;
  role: 'buyer' | 'seller';
  /** Signing position — lower signs first under 'sequential'. */
  order: number;
}

/** One attempt to sign — everything the capture surface collected. */
export interface SignatureIntent {
  signerId: string;
  method: 'typed' | 'drawn';
  /** Typed legal name, or the serialized stroke data for a drawn signature. */
  value: string;
  /** Must be exactly `true` — the explicit ESIGN/UETA consent checkbox. */
  consent: boolean;
  /** Epoch milliseconds at capture time (caller-supplied, never invented). */
  timestamp: number;
  /** Request origin, recorded verbatim. */
  ip: string;
  /** Signer's user agent, recorded verbatim. */
  userAgent: string;
}

/** A sealed link in the signer audit chain. */
export interface SignatureRecord {
  signerId: string;
  method: 'typed' | 'drawn';
  value: string;
  timestamp: number;
  ip: string;
  userAgent: string;
  /** The exact frozen doc version this signature covers. */
  docVersionHash: string;
  /** This link's seal — digests prevHash + doc + identity + value + origin. */
  entryHash: string;
  /** The previous record's entryHash ('' for the first signature). */
  prevHash: string;
}

/** The whole signing session — immutable snapshots in, new snapshots out. */
export type SigningState = {
  docVersionHash: string;
  order: 'sequential' | 'parallel';
  signers: Signer[];
  signatures: SignatureRecord[];
};

// ---------------------------------------------------------------------------
// Internals — stable serialization + digest
// ---------------------------------------------------------------------------

/**
 * Deterministic JSON with sorted object keys — key insertion order can never
 * change a hash (same approach as proposalBlocks' internal stableStringify).
 */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) {
    return '[' + value.map((v) => stableStringify(v)).join(',') + ']';
  }
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj)
    .filter((k) => obj[k] !== undefined)
    .sort();
  return (
    '{' +
    keys.map((k) => JSON.stringify(k) + ':' + stableStringify(obj[k])).join(',') +
    '}'
  );
}

/** FNV-1a 32-bit hex with a signature-chain prefix. Pure, dependency-free. */
function sigHash(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return 'sig1a:' + (h >>> 0).toString(16).padStart(8, '0');
}

/**
 * Recompute the seal for one record. Covers EVERYTHING that matters:
 * the back-link, the exact doc version, the signer's full identity, the
 * signature value itself, and the capture origin — so tampering with any
 * field (including the drawn/typed value) breaks the chain.
 */
function entryHashFor(
  prevHash: string,
  docVersionHash: string,
  signer: Signer,
  rec: Pick<SignatureRecord, 'method' | 'value' | 'timestamp' | 'ip' | 'userAgent'>,
): string {
  return sigHash(
    stableStringify({
      prevHash,
      docVersionHash,
      signer: {
        id: signer.id,
        name: signer.name,
        email: signer.email,
        role: signer.role,
        order: signer.order,
      },
      method: rec.method,
      value: rec.value,
      timestamp: rec.timestamp,
      ip: rec.ip,
      userAgent: rec.userAgent,
    }),
  );
}

// ---------------------------------------------------------------------------
// Session lifecycle
// ---------------------------------------------------------------------------

/**
 * Open a signing session over one frozen doc version. Signers are cloned
 * (the state never aliases caller objects); duplicate signer ids and an
 * empty doc hash are configuration errors and throw immediately.
 */
export function initSigning(input: {
  docVersionHash: string;
  signers: Signer[];
  order: 'sequential' | 'parallel';
}): SigningState {
  if (input.docVersionHash.trim() === '') {
    throw new Error('initSigning: docVersionHash must be a non-empty snapshot hash.');
  }
  if (input.signers.length === 0) {
    throw new Error('initSigning: at least one signer is required.');
  }
  const seen = new Set<string>();
  for (const s of input.signers) {
    if (seen.has(s.id)) {
      throw new Error(`initSigning: duplicate signer id '${s.id}'.`);
    }
    seen.add(s.id);
  }
  return {
    docVersionHash: input.docVersionHash,
    order: input.order,
    signers: input.signers.map((s) => ({ ...s })),
    signatures: [],
  };
}

/**
 * Who may sign right now. Sequential: only the unsigned signer(s) holding
 * the LOWEST outstanding `order`. Parallel: every unsigned signer.
 */
export function nextEligibleSigners(state: SigningState): Signer[] {
  const signed = new Set(state.signatures.map((r) => r.signerId));
  const unsigned = state.signers.filter((s) => !signed.has(s.id));
  if (unsigned.length === 0) return [];
  if (state.order === 'parallel') return unsigned;
  const lowest = Math.min(...unsigned.map((s) => s.order));
  return unsigned.filter((s) => s.order === lowest);
}

/**
 * Capture one signature. Throws (never silently records) when:
 *   • `consent !== true` — no ESIGN consent, no signature;
 *   • the typed/drawn value is empty — no intent artifact, no signature;
 *   • the signer id is unknown to this session;
 *   • the signer already signed;
 *   • the signer is not eligible under the session's ordering.
 * On success, returns a NEW state plus the sealed record whose `entryHash`
 * chains to the prior record — the tamper-evident signer audit trail.
 */
export function captureSignature(
  state: SigningState,
  intent: SignatureIntent,
): { state: SigningState; record: SignatureRecord } {
  if (intent.consent !== true) {
    throw new Error(
      'Signature rejected: explicit consent to sign electronically is required.',
    );
  }
  const value = intent.method === 'typed' ? intent.value.trim() : intent.value;
  if (value.trim() === '') {
    throw new Error(
      `Signature rejected: ${intent.method} signature value must not be empty.`,
    );
  }
  const signer = state.signers.find((s) => s.id === intent.signerId);
  if (!signer) {
    throw new Error(`Signature rejected: unknown signer '${intent.signerId}'.`);
  }
  if (state.signatures.some((r) => r.signerId === signer.id)) {
    throw new Error(`Signature rejected: ${signer.name} already signed.`);
  }
  if (!nextEligibleSigners(state).some((s) => s.id === signer.id)) {
    throw new Error(
      `Signature rejected: ${signer.name} is not eligible to sign yet (${state.order} order).`,
    );
  }

  const prevHash =
    state.signatures.length === 0
      ? ''
      : state.signatures[state.signatures.length - 1].entryHash;
  const core = {
    method: intent.method,
    value,
    timestamp: intent.timestamp,
    ip: intent.ip,
    userAgent: intent.userAgent,
  } as const;
  const record: SignatureRecord = {
    signerId: signer.id,
    ...core,
    docVersionHash: state.docVersionHash,
    prevHash,
    entryHash: entryHashFor(prevHash, state.docVersionHash, signer, core),
  };
  return {
    state: { ...state, signatures: [...state.signatures, record] },
    record,
  };
}

// ---------------------------------------------------------------------------
// Verification
// ---------------------------------------------------------------------------

/**
 * Walk the signer chain and recompute every seal:
 *   1. each record's `prevHash` must equal the prior record's `entryHash`
 *      ('' for the first) — splices and reorders break here;
 *   2. each record's `docVersionHash` must match the session's;
 *   3. each record's `entryHash` must equal a fresh recompute over its own
 *      fields + the signer's identity — any edit breaks here.
 * Returns the FIRST broken index; an empty chain is honestly intact.
 */
export function verifySignatureChain(state: SigningState): {
  ok: boolean;
  brokenAt: number | null;
} {
  let prevHash = '';
  for (let i = 0; i < state.signatures.length; i++) {
    const rec = state.signatures[i];
    if (rec.prevHash !== prevHash) return { ok: false, brokenAt: i };
    if (rec.docVersionHash !== state.docVersionHash) return { ok: false, brokenAt: i };
    const signer = state.signers.find((s) => s.id === rec.signerId);
    if (!signer) return { ok: false, brokenAt: i };
    const recomputed = entryHashFor(rec.prevHash, rec.docVersionHash, signer, rec);
    if (rec.entryHash !== recomputed) return { ok: false, brokenAt: i };
    prevHash = rec.entryHash;
  }
  return { ok: true, brokenAt: null };
}

/** True once every expected signer has a sealed signature. */
export function isFullyExecuted(state: SigningState): boolean {
  const signed = new Set(state.signatures.map((r) => r.signerId));
  return state.signers.every((s) => signed.has(s.id));
}

// ---------------------------------------------------------------------------
// Certificate of completion — the signed-artifact certificate
// ---------------------------------------------------------------------------

export interface CompletionCertificate {
  title: string;
  docVersionHash: string;
  signers: {
    name: string;
    email: string;
    signedAt: number;
    ip: string;
    method: string;
  }[];
  chainOk: boolean;
  certificateHash: string;
}

/**
 * Build the certificate-of-completion artifact: every captured signature
 * with the signer's identity, when/where/how they signed, the exact doc
 * version hash, and the chain verdict — all sealed under a deterministic
 * `certificateHash` (stableStringify → FNV-1a) so the certificate itself
 * is tamper-evident. Same session + summary → same hash, always.
 */
export function buildCertificate(
  state: SigningState,
  docSummary: { title: string; docVersionHash: string },
): CompletionCertificate {
  const byId = new Map(state.signers.map((s) => [s.id, s]));
  const signers = state.signatures.map((rec) => {
    const signer = byId.get(rec.signerId);
    return {
      name: signer?.name ?? rec.signerId,
      email: signer?.email ?? '',
      signedAt: rec.timestamp,
      ip: rec.ip,
      method: rec.method,
    };
  });
  const chainOk = verifySignatureChain(state).ok;
  const body = {
    title: docSummary.title,
    docVersionHash: docSummary.docVersionHash,
    signers,
    chainOk,
    entryHashes: state.signatures.map((r) => r.entryHash),
    values: state.signatures.map((r) => r.value),
  };
  return {
    title: docSummary.title,
    docVersionHash: docSummary.docVersionHash,
    signers,
    chainOk,
    certificateHash: sigHash(stableStringify(body)),
  };
}

// ---------------------------------------------------------------------------
// "What exactly did they sign" guarantee
// ---------------------------------------------------------------------------

/**
 * Throw unless the current document hashes to EXACTLY the version this
 * session's signatures cover. Call before rendering "signed" or accepting
 * further signatures — a post-send edit must invalidate the session.
 */
export function assertDocUnchangedSince(
  state: SigningState,
  currentDocHash: string,
): void {
  if (currentDocHash !== state.docVersionHash) {
    throw new Error(
      `Document changed since signing began: signed version ${state.docVersionHash}, current ${currentDocHash}. Re-issue for signature.`,
    );
  }
}

// ---------------------------------------------------------------------------
// SignaturePadModel — pure UI model for the capture surface
// ---------------------------------------------------------------------------

/** One point of a drawn stroke, in pad-local coordinates. */
export interface StrokePoint {
  x: number;
  y: number;
}

/** Pure model backing the signature pad — no DOM, no canvas, no mutation. */
export interface SignaturePadModel {
  method: 'typed' | 'drawn';
  /** Normalized typed legal name ('' until set). */
  typedValue: string;
  /** Completed drawn strokes, oldest first. */
  strokes: StrokePoint[][];
}

/** Trim + collapse internal whitespace — 'Dana   Buyer ' → 'Dana Buyer'. */
export function normalizeTypedName(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ');
}

export function createSignaturePad(method: 'typed' | 'drawn'): SignaturePadModel {
  return { method, typedValue: '', strokes: [] };
}

/** Set the typed name (normalized). Returns a new pad; input untouched. */
export function setTypedValue(pad: SignaturePadModel, raw: string): SignaturePadModel {
  return { ...pad, typedValue: normalizeTypedName(raw) };
}

/** Append one completed stroke. Returns a new pad; input untouched. */
export function addStroke(
  pad: SignaturePadModel,
  points: StrokePoint[],
): SignaturePadModel {
  return { ...pad, strokes: [...pad.strokes, points.map((p) => ({ ...p }))] };
}

/**
 * The SignatureIntent `value` for this pad: the normalized typed name, or a
 * deterministic serialization of the strokes ('' while the pad is empty —
 * which captureSignature rejects, closing the blank-signature hole).
 */
export function padValue(pad: SignaturePadModel): string {
  if (pad.method === 'typed') return pad.typedValue;
  if (pad.strokes.length === 0) return '';
  return pad.strokes
    .map((stroke) => stroke.map((p) => `${p.x},${p.y}`).join(' '))
    .join('|');
}

/** True once the pad carries a signable value. */
export function isPadComplete(pad: SignaturePadModel): boolean {
  return padValue(pad) !== '';
}
